import {t} from 'i18next';
import {FILE_TYPE} from "../../../../../Helpers/fileType";
import {EMPTY_VALUES} from "../../../../../Apis/functions";
import {
    CleanIcon,
    IconChipTools,
    IconVehicle,
    PersonOutlinedChip
} from "../../../../../components/buttons/IconButtons";
export const DEFAULT_VISITOR_TYPE = "EMPLOYEE";
// Visitor type used when the invitation does not require documentation validation.
export const NONE_VISITOR_TYPE = "NONE";

/**
 * Leaves a visitor type name comparable with the key it is looked up by, both are
 * written the same way once the spaces and the dashes become underscores.
 *
 * @param {string} [value] - Name of a type of the catalog or key it is matched against.
 * @returns {string} Value normalized, empty when there is none.
 */
export const normalizeVisitorTypeName = (value) => (value ?? '').trim().toUpperCase().replace(/[\s-]+/g, '_');

/**
 * Visitor types the platform seeds, the only ones with a translation of their
 * own: the types a company registers on the catalog are named by whoever created
 * them, so they are painted with that name as it was captured.
 *
 * Keyed by the name visitor-type-service returns, already normalized. The icon is
 * only carried by the ones offered as a card.
 */
export const DEFAULT_VISITOR_TYPES = {
    [DEFAULT_VISITOR_TYPE]: {
        key: DEFAULT_VISITOR_TYPE,
        translation: 'employee',
    },
    [NONE_VISITOR_TYPE]: {
        key: NONE_VISITOR_TYPE,
        translation: 'none',
    },
    PERSONAL_GUEST: {
        key: 'PERSONAL_GUEST',
        translation: 'personal_guest',
        icon: <PersonOutlinedChip width={24} height={24}/>,
    },
    HOUSEHOLD_STAFF: {
        key: 'HOUSEHOLD_STAFF',
        translation: 'household_staff',
        icon: <CleanIcon width={24} height={24}/>,
    },
    PROVIDER_MAINTENANCE: {
        key: 'PROVIDER_MAINTENANCE',
        translation: 'provider_maintenance',
        icon: <IconChipTools width={24} height={24}/>,
    },
    DELIVERY: {
        key: 'DELIVERY',
        translation: 'delivery',
        icon: <IconVehicle width={24} height={24}/>,
    },
}

/**
 * Types a resident invites from My apartments, one card each. EMPLOYEE and NONE
 * belong to the invitations of the company, they are not offered there.
 */
export const INVITABLE_VISITOR_TYPES = [
    DEFAULT_VISITOR_TYPES.PERSONAL_GUEST,
    DEFAULT_VISITOR_TYPES.HOUSEHOLD_STAFF,
    DEFAULT_VISITOR_TYPES.PROVIDER_MAINTENANCE,
    DEFAULT_VISITOR_TYPES.DELIVERY,
]

/**
 * Name of a visitor type as it has to be painted. The catalog returns it in upper
 * case (PERSONAL_GUEST), the seeded types are translated and the ones the company
 * registered keep the name it captured.
 *
 * @param {Function} translate - Localization function.
 * @param {string} [name] - Name of the type as the catalog returns it.
 * @returns {string} Name of the type, the default empty value when there is none.
 */
export const getVisitorTypeName = (translate, name) => {
    if (!name) return EMPTY_VALUES.DEFAULT;

    const defaultType = DEFAULT_VISITOR_TYPES[normalizeVisitorTypeName(name)];

    return defaultType ? translate(defaultType.translation) : name;
}

/**
 * Name of the visitor type a visit was created with, resolved the same way but
 * out of the type object the invitation carries.
 *
 * @param {Function} translate - Localization function.
 * @param {Object} [visitorType] - Visitor type of the visit.
 * @returns {string} Name of the type, the default empty value when there is no type.
 */
export const getVisitorTypeLabel = (translate, visitorType) => getVisitorTypeName(translate, visitorType?.name);

const { TextFieldChip,
    NumericFieldChip,
    FileUploadChip,
    DatePickerChip,
    TimePickerChip,
    DateTimePickerChip,
    CheckboxChip} = require("../../../../../components/buttons/IconButtons");


export const FIELD_TYPES_MODEL = () => {
    return [
        { id: "TEXT", name: t("text_field"), icon: TextFieldChip },
        { id: "NUMERIC", name: t("numeric_field"), icon: NumericFieldChip },
        { id: "DOCUMENT", name: t("file_upload"), icon: FileUploadChip },
        { id: "DATE", name: t("date_picker"), icon: DatePickerChip },
        { id: "TIME", name: t("time_picker"), icon: TimePickerChip },
        { id: "DATE_TIME", name: t("date_time_picker"), icon: DateTimePickerChip },
        { id: "CHECKBOX", name: t("checkbox"), icon: CheckboxChip },
    ];
}

export const FIELD_TYPE ={
    TEXT : {name:'TEXT',length:500, position:1 },
    NUMERIC : {name:'NUMERIC',length:64, position:2 },
    DATE : {name:'DATE', length: 0, position:3 },
    TIME :{name:'TIME', length: 0, position:4 },
    DATE_TIME :{name:'DATE_TIME', length:0, position:5 },
    CHECKBOX :{name:'CHECKBOX', length: 0, position:6 },
    DOCUMENT : {name:'DOCUMENT', length: FILE_TYPE.IMAGE_FILES_VIDEOS.size,  position:7 },
}

export function sortListFieldType(fields) {
    if (!Array.isArray(fields) || fields.length === 0) {
        return [];
    }
    return [...fields].sort((a, b) => {
        const posA = FIELD_TYPE[a.fieldType]?.position ?? 999;
        const posB = FIELD_TYPE[b.fieldType]?.position ?? 999;
        return posA - posB;
    });
}

export const VISITOR_TYPE_MODEL = {
    id: null,
    name: '',
    fields:[],
    isValid: function () {
        return this.name?.trim() !== '';
    },
    isValidFields: function (fields) {
        return fields?.length > 0;
    },
    toRegister: function (fields) {
        return {
            id: this.id,
            name: this.name,
            fields: fields.map(e => {
                if(typeof e.id === 'string') return e;
                else return {...e, id:null};
            }),
        }
    },
    set: function (data) {
        return {
            ...this,
            id: data.id,
            name: data.name,
            fields: []
        }
    },
}

export const CUSTOM_FIELD_MODEL = {
    id: null,
    fieldName: '',
    fieldType: null,
    isRequired: false,
    isValid: function (){
        return this.fieldName?.trim() !== '' && this.fieldType?.trim() !== '' && this.fieldType != null
    }
};

export const getFieldTypeById = (id) => {
    const row = FIELD_TYPES_MODEL().find(e => e.id === id);
    return row || null;
};

export const getIndexFieldByAllData = (list, field) => {
    return list.findIndex(e => e.id === field.id && e.fieldName === field.fieldName && e.fieldType === field.fieldType && e.isRequired === field.isRequired);
};
